import { useTranslation } from "react-i18next";
import ActionButton from "./ActionButton";
import Field from "./Field";

// ─── Component ─────────────────────────────────────────────────

/**
 * CodeEntryScreen
 *
 * Manual code entry. Uppercases as the user types and allows submit on
 * Enter. The submit button stays disabled while the field is empty or
 * a submission is in flight.
 *
 * Props:
 *   value      — current input value (controlled)
 *   onChange   — receives the uppercased next value
 *   onBack     — return to ChoiceScreen
 *   onSubmit   — trigger validation + switch to coded flow on success
 *   error      — error string to render below the input (null-able)
 *   submitting — disable inputs while validating
 */
const CodeEntryScreen = ({
  value,
  onChange,
  onBack,
  onSubmit,
  error,
  submitting,
}) => {
  const { t } = useTranslation();
  const trimmed = value.trim();
  const canSubmit = Boolean(trimmed) && !submitting;

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && canSubmit) {
      e.preventDefault();
      onSubmit();
    }
  };

  return (
    <div style={{ textAlign: "center", padding: "16px 0 8px" }}>
      <p
        id="rsvp-modal-title"
        className="heading-60"
        style={{ marginBottom: 20 }}
      >
        {t("rsvp.codeEntry.title")}
      </p>
      <p className="body-16" style={{ opacity: 0.8, marginBottom: 30 }}>
        {t("rsvp.codeEntry.subtitle")}
      </p>
      <div
        style={{
          marginBottom: 20,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Field
          type="text"
          // placeholder="e.g. X8K2M4"
          value={value}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          onKeyDown={handleKeyDown}
          maxLength={8}
          disabled={submitting}
          autoFocus
          style={{
            textAlign: "center",
            width: 260,
            letterSpacing: 4,
            fontFamily: "monospace",
            fontSize: 18,
            // textTransform: "uppercase",
          }}
        />
      </div>
      {error && (
        <p
          className="body-16"
          style={{
            color: "var(--black)",
            opacity: 0.7,
            textAlign: "center",
            marginBottom: 20,
            maxWidth: 400,
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          {error}
        </p>
      )}
      <div
        style={{
          display: "flex",
          gap: 16,
          justifyContent: "center",
          flexWrap: "wrap",
        }}
      >
        <ActionButton variant="ghost" onClick={onBack} disabled={submitting}>
          {t("rsvp.buttons.back")}
        </ActionButton>
        <ActionButton
          variant="primary"
          onClick={onSubmit}
          disabled={!canSubmit}
        >
          {submitting ? t("rsvp.buttons.checking") : t("rsvp.buttons.continue")}
        </ActionButton>
      </div>
    </div>
  );
};

export default CodeEntryScreen;
